import { applyFeatureFlagOperations, type FeatureFlagOp } from '@shared';

type OpScope =
  | { kind: 'allAdvisors'; flagKey: string }
  | { kind: 'advisor'; advisorId: string; flagKey: string }
  | { kind: 'advisor-section'; advisorId: string };

function opScope(op: FeatureFlagOp): OpScope {
  switch (op.type) {
    case 'SET_ALL_ADVISORS':
    case 'REMOVE_ALL_ADVISORS':
      return { kind: 'allAdvisors', flagKey: op.flagKey };
    case 'SET_ADVISOR_FLAG':
    case 'REMOVE_ADVISOR_FLAG':
      return { kind: 'advisor', advisorId: op.advisorId, flagKey: op.flagKey };
    case 'REMOVE_ADVISOR_SECTION':
      return { kind: 'advisor-section', advisorId: op.advisorId };
  }
}

function scopesOverlap(a: OpScope, b: OpScope): boolean {
  if (a.kind === 'allAdvisors' && b.kind === 'allAdvisors') {
    return a.flagKey === b.flagKey;
  }
  if (a.kind === 'advisor-section' && b.kind === 'advisor-section') {
    return a.advisorId === b.advisorId;
  }
  if (a.kind === 'advisor' && b.kind === 'advisor') {
    return a.advisorId === b.advisorId && a.flagKey === b.flagKey;
  }
  // REMOVE_ADVISOR_SECTION supersedes any per-flag op for that advisor.
  if (a.kind === 'advisor-section' && b.kind === 'advisor') {
    return a.advisorId === b.advisorId;
  }
  if (a.kind === 'advisor' && b.kind === 'advisor-section') {
    return a.advisorId === b.advisorId;
  }
  return false;
}

/**
 * Append a new op to the draft, stripping any prior ops that target the same
 * scope and dropping the appended op if it restores the original value.
 */
export function reconcileOperation(
  original: AppConfigSchema,
  currentOps: readonly FeatureFlagOp[],
  nextOp: FeatureFlagOp,
): FeatureFlagOp[] {
  const nextScope = opScope(nextOp);
  const pruned = currentOps.filter(op => !scopesOverlap(nextScope, opScope(op)));

  const candidate = [...pruned, nextOp];
  const afterCandidate = applyFeatureFlagOperations(original, candidate);
  const afterPruned = applyFeatureFlagOperations(original, pruned);

  // If the new op has no net effect (e.g. toggled back to original), drop it.
  if (
    JSON.stringify(canonicalAdvisorPart(afterCandidate)) ===
    JSON.stringify(canonicalAdvisorPart(afterPruned))
  ) {
    return pruned;
  }

  if (
    JSON.stringify(canonicalAdvisorPart(afterCandidate)) ===
    JSON.stringify(canonicalAdvisorPart(original))
  ) {
    return pruned;
  }

  return candidate;
}

function canonicalAdvisorPart(config: AppConfigSchema) {
  return {
    allAdvisors: sortKeys(config.allAdvisors ?? {}),
    advisors: Object.fromEntries(
      Object.keys(config.advisors ?? {})
        .sort()
        .map(id => [id, sortKeys((config.advisors ?? {})[id] ?? {})]),
    ),
  };
}

function sortKeys(record: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(record).sort()) out[key] = record[key];
  return out;
}

export function removeOperationAtIndex(
  ops: readonly FeatureFlagOp[],
  index: number,
): FeatureFlagOp[] {
  return ops.filter((_, i) => i !== index);
}
